import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // ── PERMANENT FIX (Option A) ──────────────────────────────────────────
    // A same-product variant change (size / price) must NEVER replace the
    // gallery DOM. Price, compare-at price, availability, SKU and buy-button
    // state are each updated IN PLACE by their own components listening to the
    // same variantUpdate event (product-price, product-sku, product-inventory,
    // product-form / sticky-add-to-cart). For a same-product change, Horizon
    // morphs only the variant picker — it does NOT morph <main> — so this
    // media-gallery handler is the SOLE thing that would touch the gallery.
    // We therefore do nothing and leave the customer's chosen image exactly as
    // it is. The gallery only ever changes on a thumbnail click or mobile swipe.
    //
    // This is immune to whatever media Printful assigns to variants (it keeps
    // re-attaching them on every sync): the fetched gallery HTML is simply
    // never applied here. `newProduct` is set only for a combined-listing swap
    // to a genuinely different product, where fresh imagery IS expected — that
    // path keeps the old restore-by-media-id behaviour below.
    const isNewProduct = Boolean(event.detail && event.detail.data && event.detail.data.newProduct);
    if (!isNewProduct) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Combined-listing product swap: accept the new gallery, but keep the
    // active media if it still exists (captured before swap, restored after).
    const keyOf = (slide) => {
      if (!slide) return '';
      const mediaId = slide.getAttribute('data-media-id');
      if (mediaId) return 'id:' + mediaId;
      const img = slide.querySelector('img');
      return img ? 'src:' + (img.getAttribute('src') || '') : '';
    };
    const oldSlides = Array.from(this.querySelectorAll('slideshow-slide'));
    const newSlides = Array.from(newMediaGallery.querySelectorAll('slideshow-slide'));
    const activeIndex = oldSlides.findIndex((s) => s.getAttribute('aria-hidden') === 'false');
    const activeKey = activeIndex >= 0 ? keyOf(oldSlides[activeIndex]) : '';

    let restoreIndex = activeKey ? newSlides.findIndex((s) => keyOf(s) === activeKey) : -1;
    if (restoreIndex < 0 && activeIndex >= 0 && activeIndex < newSlides.length) {
      restoreIndex = activeIndex;
    }

    if (restoreIndex > -1) {
      newSlides.forEach((s, i) => s.setAttribute('aria-hidden', i === restoreIndex ? 'false' : 'true'));
      newMediaGallery
        .querySelectorAll('.slideshow-controls__thumbnail')
        .forEach((t, i) => t.setAttribute('aria-selected', `${i === restoreIndex}`));

      // The fresh slideshow initializes from [initial-slide] (server sends 0);
      // point it at the restored slide so its own init agrees with the DOM.
      const newSlideshow = newMediaGallery.querySelector('slideshow-component');
      if (newSlideshow) newSlideshow.setAttribute('initial-slide', String(restoreIndex));

      // After upgrade, sync internal state + mobile scroll position.
      const idx = restoreIndex;
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          const slideshow = newMediaGallery.querySelector('slideshow-component');
          if (slideshow && typeof slideshow.select === 'function') {
            try {
              slideshow.select(idx, undefined, { animate: false });
            } catch (e) {
              /* attributes above already carry the visual state */
            }
          }
        });
      });
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
